import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, UserRound } from 'lucide-react';
import { PracticeArea, UserDetails, UserDetailsInput } from '../types';
import { isUserDetailsComplete } from '../services/userDetailsValidation';

interface UserDetailsModalProps {
  isOpen: boolean;
  initialDetails: UserDetails | null;
  onSave: (details: UserDetailsInput) => Promise<void>;
}

const PRACTICE_AREAS: PracticeArea[] = [
  'Civil/Comercial',
  'Penal',
  'Laboral',
  'Administrativo',
  'Familia',
  'Previsional',
  'Otro',
];

const emptyDetails: UserDetailsInput = {
  firstName: '',
  lastName: '',
  organization: '',
  city: '',
  whatsapp: '',
  practiceAreas: [],
};

const inputClassName = "w-full bg-zinc-950/60 border border-zinc-700 rounded-xl py-3 px-4 text-gray-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all";

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ isOpen, initialDetails, onSave }) => {
  const [form, setForm] = useState<UserDetailsInput>({
    ...emptyDetails,
    ...initialDetails,
    practiceAreas: initialDetails?.practiceAreas || [],
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      ...emptyDetails,
      ...initialDetails,
      practiceAreas: initialDetails?.practiceAreas || [],
    });
    setHasSubmitted(false);
    setIsSaving(false);
    setError(null);
  }, [initialDetails, isOpen]);

  const isComplete = isUserDetailsComplete(form);

  if (!isOpen) return null;

  const updateField = (field: keyof Omit<UserDetailsInput, 'practiceAreas'>, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const togglePracticeArea = (area: PracticeArea) => {
    setForm((current) => {
      const exists = current.practiceAreas.includes(area);
      return {
        ...current,
        practiceAreas: exists
          ? current.practiceAreas.filter((item) => item !== area)
          : [...current.practiceAreas, area],
      };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);
    setError(null);

    if (!isComplete) {
      setError('Completá todos los campos y seleccioná al menos un área de práctica.');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(form);
    } catch (saveError) {
      console.error('Failed to save user details', saveError);
      setError('No pudimos guardar tus datos. Intentá nuevamente.');
      setIsSaving(false);
    }
  };

  const showValidation = hasSubmitted && !isComplete;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[80]" />
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <form onSubmit={handleSubmit} className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
              <UserRound className="w-6 h-6 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Completá tus datos para continuar</h2>
            <p className="text-sm text-zinc-400 mt-2">
              ¡Nos alegra que estés disfrutando de LegalPromptStudio, que es y seguirá siendo 100% gratuito! Queremos conocer a nuestros usuarios y cómo los estamos ayudando. Por favor, antes de continuar, completá los siguientes datos. Son 100% confidenciales y no se comparten con nadie.
            </p>
          </div>

          <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="p-3 bg-red-950/40 border border-red-900/50 rounded-xl flex items-start gap-2 text-red-200 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className={inputClassName} placeholder="Nombre" value={form.firstName} onChange={(event) => updateField('firstName', event.target.value)} />
              <input className={inputClassName} placeholder="Apellido" value={form.lastName} onChange={(event) => updateField('lastName', event.target.value)} />
              <input className={`${inputClassName} sm:col-span-2`} placeholder="Estudio, tribunal u organización" value={form.organization} onChange={(event) => updateField('organization', event.target.value)} />
              <input className={inputClassName} placeholder="Ciudad" value={form.city} onChange={(event) => updateField('city', event.target.value)} />
              <input className={inputClassName} placeholder="Whatsapp" value={form.whatsapp} onChange={(event) => updateField('whatsapp', event.target.value)} />
            </div>

            <div>
              <p className="text-sm font-semibold text-zinc-200 mb-3">Principales áreas de práctica</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PRACTICE_AREAS.map((area) => {
                  const selected = form.practiceAreas.includes(area);
                  return (
                    <button
                      key={area}
                      type="button"
                      onClick={() => togglePracticeArea(area)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                        selected
                          ? 'border-cyan-500/60 bg-cyan-950/30 text-cyan-100'
                          : 'border-zinc-700 bg-zinc-800/30 text-zinc-300 hover:bg-zinc-800'
                      }`}
                    >
                      <span>{area}</span>
                      {selected && <Check className="w-4 h-4 text-cyan-400" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {showValidation && (
              <p className="text-xs text-red-300">Todos los campos son obligatorios.</p>
            )}
          </div>

          <div className="p-5 border-t border-zinc-800 bg-black/20 flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="bg-white hover:bg-gray-200 text-black font-bold py-3 px-6 rounded-xl transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              Guardar y analizar
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default UserDetailsModal;
